/**
 * Companies Management Page
 * Super admin can view and manage all companies
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Edit, Trash2, Database, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import CompanyModal from '../../components/admin/CompanyModal';
import { useDataSourceStore } from '../../store/dataSourceStore';

interface Company {
  _id: string;
  name: string;
  slug: string;
  domain?: string;
  subscription: {
    plan: string;
    status: string;
    maxUsers: number;
  };
  dataSources?: any[];
  isActive: boolean;
  createdAt: string;
}

const CompaniesPage = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | undefined>();
  const { setSelectedCompany: setGlobalCompany, selectedCompany: globalCompany } =
    useDataSourceStore();

  const { data, isLoading, error } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get('/api/v1/companies').then(res => res.data.data),
  });

  const companies: Company[] = data || [];

  const handleQuickSelect = (company: Company) => {
    setGlobalCompany(company);
    toast.success(`Switched to ${company.name}`, {
      icon: '🏢',
      duration: 2000,
    });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/companies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Company deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to delete company');
    },
  });

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedCompany(undefined);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (
      window.confirm('Are you sure you want to delete this company? This action cannot be undone.')
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedCompany(undefined);
  };

  const getPlanBadge = (plan: string) => {
    const colors: Record<string, string> = {
      free: 'badge bg-secondary-100 text-secondary-800 dark:bg-secondary-700 dark:text-secondary-300',
      starter: 'badge bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      professional: 'badge bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      enterprise: 'badge bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300',
    };
    return colors[plan] || colors.free;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'badge-success',
      inactive: 'badge-secondary',
      suspended: 'badge-warning',
      cancelled: 'badge-danger',
    };
    return `badge ${colors[status] || 'badge-secondary'}`;
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Info Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 shadow-sm animate-slide-down">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
              Super Admin: Multi-Tenant Management
            </h4>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Click on a company row to <strong>quickly switch context</strong> for managing users,
              roles, and viewing analytics. The selected company is shown in the header selector.
              {globalCompany && (
                <span className="block mt-1 font-semibold">
                  Currently viewing:{' '}
                  <span className="text-blue-900 dark:text-blue-100">{globalCompany.name}</span>
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between animate-slide-down delay-100">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">🏢 Companies</h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-1">
            Manage all companies in the platform
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="btn btn-primary flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          New Company
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="spinner"></div>
        </div>
      )}

      {error && (
        <div className="card bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <p className="text-red-600 dark:text-red-400">
            Failed to load companies. Please try again.
          </p>
        </div>
      )}

      {!isLoading && !error && (
        <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-xl overflow-hidden animate-slide-up delay-200">
          <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
            <thead className="bg-secondary-50 dark:bg-secondary-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Data Sources
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Max Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
              {companies.map((company, index) => (
                <tr
                  key={company._id}
                  onClick={() => handleQuickSelect(company)}
                  className={`transition-all duration-200 cursor-pointer animate-fade-in ${
                    globalCompany?._id === company._id
                      ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-600'
                      : 'hover:bg-secondary-50 dark:hover:bg-secondary-700'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  title="Click to switch context to this company"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-300" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-secondary-900 dark:text-white">
                          {company.name}
                        </div>
                        <div className="text-sm text-secondary-500 dark:text-secondary-400">
                          {company.domain || company.slug}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getPlanBadge(company.subscription.plan)}>
                      {company.subscription.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(company.subscription.status)}>
                      {company.subscription.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-400">
                      <Database className="w-4 h-4" />
                      {company.dataSources?.length || 0}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600 dark:text-secondary-400">
                    {company.subscription.maxUsers}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600 dark:text-secondary-400">
                    {new Date(company.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div
                      className="flex items-center justify-end gap-2"
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        onClick={() => handleEdit(company)}
                        className="p-2 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-all duration-200 transform hover:scale-110"
                        title="Edit company"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(company._id)}
                        disabled={deleteMutation.isPending}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete company"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {companies.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-16 h-16 mx-auto text-secondary-300 dark:text-secondary-600 mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                No companies yet
              </h3>
              <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                Get started by creating your first company.
              </p>
              <button onClick={handleAdd} className="btn btn-primary">
                Create Company
              </button>
            </div>
          )}
        </div>
      )}

      {/* Company Modal */}
      <CompanyModal isOpen={isModalOpen} onClose={handleModalClose} company={selectedCompany} />
    </div>
  );
};

export default CompaniesPage;
